import { ChangeEvent, useEffect } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemProps,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { CatalogProductType } from '../enums'
import { CatalogProduct } from '../types'
import {
  SpineMultimeasurementCatalogProduct,
  SpineMultimeasurementOutput
} from '../types/CatalogProduct'
import { SpineMultimeasurementCatalogProductCustomDataInput } from '../types/CatalogProductInput'
import CatalogProductForm from './CatalogProductForm'
import NominalCurrentSelect from './manufacturing/NominalCurrentSelect'
import ClampRatioField from './manufacturing/ClampRatioField'

export interface SpineMultimeasurementCatalogProductFormProps {
  catalogProduct?: SpineMultimeasurementCatalogProduct
  onCancel?: () => void
  onError?: (error: unknown) => void
  afterSubmit?: (catalogProduct: CatalogProduct) => void
}

export default function SpineMultimeasurementCatalogProductForm({
  catalogProduct,
  ...props
}: SpineMultimeasurementCatalogProductFormProps) {
  return (
    <CatalogProductForm
      {...props}
      type={CatalogProductType.SPINE_MULTI_MEASUREMENT}
      catalogProduct={catalogProduct}
    />
  )
}

/**
 * The custom data fields of a spine multi measurement catalog product, rendered as a grid item.
 */
export function SpineMultimeasurementCatalogProductCustomDataFields({
  value,
  disabled = false,
  onChange
}: {
  value: SpineMultimeasurementCatalogProductCustomDataInput
  catalogProduct?: CatalogProduct
  disabled?: boolean
  isError?: (field: string) => boolean
  getHelperText?: (field: string) => string | undefined
  onChange: (value: SpineMultimeasurementCatalogProductCustomDataInput) => void
}) {
  const { t } = useTranslation('catalogProduct')

  return (
    <Grid item xs={12}>
      <OutputsManager
        value={value.outputs}
        disabled={disabled}
        onChange={(outputs) => onChange({ ...value, outputs })}
      />

      <FormControl fullWidth sx={{ mt: 4 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value.shipAsInstallationKit)}
              disabled={disabled}
              onChange={(_, checked) => onChange({ ...value, shipAsInstallationKit: checked })}
            />
          }
          label={t('shipsAsInstallationKit')}
        />
      </FormControl>
    </Grid>
  )
}

interface OutputsManagerProps {
  value: SpineMultimeasurementCatalogProductCustomDataInput['outputs']
  disabled: boolean
  onChange: (value: SpineMultimeasurementCatalogProductCustomDataInput['outputs']) => void
}

function OutputsManager({ value, disabled, onChange }: OutputsManagerProps) {
  const { t } = useTranslation('catalogProduct')

  const addOutput = (): void => {
    if (value) {
      const outputs = value
      const nextOutputNumber = Object.keys(outputs).length + 1
      onChange({
        ...value,
        [nextOutputNumber]: {
          nominalCurrent: 100,
          clampRatio: 1000
        }
      })
    } else {
      onChange({ 1: { nominalCurrent: 100, clampRatio: 1000 } })
    }
  }

  const handleRemove = (index: string) => {
    if (value) {
      const outputs = value
      delete outputs[index]

      // Remap indexed from 1 to n without gaps
      const remappedOutputs: SpineMultimeasurementCatalogProductCustomDataInput['outputs'] = {}
      Object.keys(outputs).forEach((key, i) => {
        remappedOutputs[i + 1] = outputs[key]
      })

      onChange(remappedOutputs)
    }
  }

  const handleChange = (index: string, outputValue: SpineMultimeasurementOutput) => {
    const outputs = value !== undefined ? value : {}
    onChange({
      ...outputs,
      [index]: outputValue
    })
  }

  if (value === undefined) {
    return null
  }

  const outputIndexes = Object.keys(value)

  return (
    <Stack spacing={3}>
      <Typography variant="subtitle2">{t('outputs')}</Typography>
      {outputIndexes.length === 0 ? (
        <Alert severity="warning">{t('noOutputs')}</Alert>
      ) : (
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {outputIndexes.map((index) => (
            <OutputListItem
              key={index}
              index={index}
              value={value[index]}
              disabled={disabled}
              disableGutters
              onValueChange={(outputValue) => handleChange(index, outputValue)}
              secondaryAction={
                outputIndexes.length > 1 && (
                  <Tooltip title={t('actions:remove')}>
                    <span>
                      <IconButton
                        aria-label={t('actions:remove')}
                        color="error"
                        disabled={disabled}
                        onClick={() => handleRemove(index)}
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              }
            />
          ))}
        </List>
      )}
      <Button
        variant="outlined"
        startIcon={<Add />}
        disabled={disabled}
        sx={{ alignSelf: 'flex-start' }}
        onClick={addOutput}
      >
        {t('addOutput')}
      </Button>
    </Stack>
  )
}

interface OutputListItemProps extends Omit<ListItemProps, 'value' | 'children'> {
  index: string
  value: SpineMultimeasurementOutput
  disabled: boolean
  onValueChange: (value: SpineMultimeasurementOutput) => void
}

function OutputListItem({ index, value, disabled, onValueChange, ...props }: OutputListItemProps) {
  const { t } = useTranslation('catalogProduct')

  const handleNominalCurrentChange = (nominalCurrent: number) => {
    onValueChange({
      ...value,
      nominalCurrent
    })
  }

  const handleClampRatioChange = (clampRatio: number | null) => {
    // The clamp ratio is required, so an empty value is ignored.
    if (clampRatio !== null) {
      onValueChange({
        ...value,
        clampRatio
      })
    }
  }

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onValueChange({
      ...value,
      name: event.target.value
    })
  }

  useEffect(() => {
    if (value.nominalCurrent === undefined) {
      return
    }

    if (value.nominalCurrent === 400) {
      onValueChange({
        ...value,
        clampRatio: 4000
      })
    } else if (value.nominalCurrent === 600) {
      onValueChange({
        ...value,
        clampRatio: 6000
      })
    } else if (value.nominalCurrent <= 100) {
      onValueChange({
        ...value,
        clampRatio: 1000
      })
    } else {
      onValueChange({
        ...value,
        clampRatio: 2500
      })
    }
  }, [value?.nominalCurrent])

  return (
    <ListItem {...props}>
      <ListItemIcon sx={{ minWidth: 32, maxWidth: 32, mx: 1, justifyContent: 'center' }}>
        <Typography>{index}</Typography>
      </ListItemIcon>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            label={t('common:name')}
            value={value?.name !== undefined ? value.name : `${index}`}
            disabled={disabled}
            fullWidth
            onChange={handleNameChange}
          />
        </Grid>
        <Grid item xs={4}>
          <NominalCurrentSelect
            value={value?.nominalCurrent !== undefined ? value.nominalCurrent : null}
            disabled={disabled}
            onValueChange={handleNominalCurrentChange}
          />
        </Grid>
        <Grid item xs={4}>
          <ClampRatioField
            value={value?.clampRatio !== undefined ? value.clampRatio : null}
            disabled={disabled}
            onValueChange={handleClampRatioChange}
          />
        </Grid>
      </Grid>
    </ListItem>
  )
}
